// Типографика по СТО (4.1) «на лету» и по команде:
//   «ёлочки» (вложенные “лапки”), « - » -> « – », «—» -> «–», диапазоны 1–4, «...» -> «…», без двойных пробелов и
//   пробелов перед знаками препинания, «№ 5», «50 %», «т. е.», «и т. д.», «А. Д. Шилов» - через ОБЫЧНЫЙ пробел;
//   по команде - все правила writer (lib/sto_lib.TYPO_RULES, typo_rules.ts): «-20» -> «−20», «25*10-3» -> «25·10−3»,
//   «3х380» -> «3×380», «2.5 мм» -> «2,5 мм», «40°С» -> «40 °С», «5 вт» -> «5 Вт», «мм/сек» -> «мм/с», «2004г» ->
//   «2004 г.», «2-х» -> «2», «ток–напряжение» -> «ток – напряжение», «< минимума» -> «меньше минимума», …;
//   в тексте они подсвечены (inlineLint: «при экспорте исправится автоматически»).
//   Неразрывные пробелы (рисунке 3, 5 кг, после «№», в инициалах…) - только при USE_NBSP (core/schema/constants, как
//   lib/sto_lib.USE_NBSP); пока он выключен, набранный или вставленный неразрывный пробел становится обычным.
// Код, формулы, ссылки и URL не затрагиваются. Автозамена - отдельный шаг истории: Ctrl+Z (или Backspace сразу
// после замены) возвращает набранное как было. Переключатель - команда sto.typoToggle (настройка браузера).
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/core';
import { blockText, applyEdits, MASK } from './textmap';
import { prefs } from './profile';
import { NBSP, hasNbsp, plainSpaces } from '../../core/schema/constants';
import { typoEdits, resolveEdits, multSignForText } from './typo_rules';

/** «Неразрывный» пробел типографики: U+00A0 только при USE_NBSP, иначе обычный пробел */
const NB = NBSP;
const EN = '–';
export const UNITS = '(?:байт(?:а|ов|ы)?|бит(?:а|ов|ы)?|[КМГТ]байт(?:а|ов)?|[КкМГТ]Б|[КкМГ]бит/с|бит/с|[КМГТ]иБ|мс|мкс|нс|мин|[кМГ]?Гц|мВ|кВ|мА|к?Вт|[кМ]?Ом|мм|см|км|кг|мг|°[CС]|пт|dpi|px|ГБ|МБ|КБ|м|с|ч|г|В|А|л|т)';
const REF_WORDS = '(?:рисун[а-яё]*|таблиц[а-яё]*|листинг[а-яё]*|формул[а-яё]*|приложени[а-яё]*|раздел[а-яё]*|подраздел[а-яё]*|пункт[а-яё]*|глав[а-яё]*|рис\\.|табл\\.|с\\.|стр\\.|п\\.|пп\\.|гл\\.|разд\\.|ч\\.|т\\.|№|§)';
const NO_TYPO_TYPES = new Set(['listing', 'formula', 'codeBlock']);

// ------------------------------------------------------------------------------------------------ кавычки
/** Кавычка для позиции: открывающая после пробела/скобки/начала, иначе закрывающая; вложенность - “лапки”. */
function quoteFor(before: string): string {
  const open = before === '' || /[\s(\[{«„“ –—\-/￼]$/.test(before);
  const depth = (before.match(/«/g) || []).length - (before.match(/»/g) || []).length;
  if (open) return depth > 0 ? '“' : '«';
  const inner = (before.match(/“/g) || []).length - (before.match(/”/g) || []).length;
  return inner > 0 ? '”' : '»';
}

// ------------------------------------------------------------------------------------------------ правила по тексту
// Правила - в typo_rules.ts: те же, что у writer при выгрузке (lib/sto_lib.TYPO_RULES, тот же порядок и область
// правил); сверка с Python - tests/web/test_typography_parity.py.
export { typoEdits, type TypoEdit, type TypoCtx } from './typo_rules';

function eligible(node: PMNode, parent: PMNode | null): boolean {
  return node.isTextblock && !node.type.spec.code && !(parent && NO_TYPO_TYPES.has(parent.type.name));
}
const CELL_TYPES = new Set(['tableCell', 'tableHeader']);

/** Знак умножения всей работы (4.6.2.4) - по тексту и формулам документа, как writer (sto_lib.mult_sign_for_text). */
function docMultSign(doc: PMNode): string {
  let t = '';
  doc.descendants((n) => {
    if (n.isText) t += n.text || '';
    else if (typeof n.attrs?.latex === 'string') t += ' ' + n.attrs.latex + ' ';
    return true;
  });
  return multSignForText(t);
}

/** Применить типографику к документу (или к узлам с id из списка). Одна транзакция = один Ctrl+Z. Как writer:
 *  правила по порядку, абзац в ячейке таблицы - как ячейка (десятичная точка -> запятая, знаки «=» не трогаются),
 *  один знак умножения на всю работу; повторные проходы по исправленному тексту (не больше 4) - до того же
 *  результата, что у writer. */
export function applyTypography(editor: Editor, onlyIds?: Set<string>, codes?: Set<string>): number {
  const { state } = editor;
  const tr = state.tr;
  const mult = docMultSign(state.doc);
  const targets: { pos: number; cell: boolean }[] = [];
  const visit = (node: PMNode, pos: number, parent: PMNode | null, inside: boolean, cell: boolean) => {
    const own = !onlyIds || inside || (node.attrs?.id && onlyIds.has(node.attrs.id));
    if (eligible(node, parent)) {
      if (own) targets.push({ pos, cell });
      return;
    }
    const inCell = cell || CELL_TYPES.has(node.type.name);
    node.forEach((c, off) => visit(c, pos + 1 + off, node, !!own && !!onlyIds, inCell));
  };
  state.doc.forEach((c, off) => visit(c, off, state.doc, false, false));
  let n = 0;
  for (const t of targets) {
    for (let pass = 0; pass < 4; pass++) {
      const pos = tr.mapping.map(t.pos);
      const node = tr.doc.nodeAt(pos);
      if (!node || !node.isTextblock) break;
      const bt = blockText(node, pos);
      const edits = resolveEdits(typoEdits(bt.text, { cell: t.cell, mult }).filter((e) => !codes || codes.has(e.code)));
      if (!edits.length) break;
      applyEdits(tr, bt, edits, tr.steps.length);
      n += edits.length;
    }
  }
  if (n) editor.view.dispatch(tr.scrollIntoView());
  return n;
}

// ------------------------------------------------------------------------------------------------ автозамена при вводе
interface Repl { del: number; insert: string }

/** Правило для набранного символа c при тексте перед курсором before. del - сколько символов before заменить
 *  (вместе с набранным символом). Замена «на то же самое» (без USE_NBSP «неразрывный» пробел - обычный) - null. */
export function typedRule(c: string, before: string): Repl | null {
  const r = typedRule0(c, before);
  if (!r) return null;
  const original = (r.del ? before.slice(before.length - r.del) : '') + c;
  return r.insert === original ? null : r;
}

function typedRule0(c: string, before: string): Repl | null {
  // набранный неразрывный пробел/дефис (Alt+0160, раскладка) - обычным (пробел после пробела не нужен)
  if (hasNbsp(c)) return plainSpaces(c) === ' ' && /[ \u00a0]$/.test(before) ? { del: 0, insert: '' } : { del: 0, insert: plainSpaces(c) };
  const lastTok = before.split(/\s/).pop() || '';
  if (/:\/\/|www\.|@|\\/.test(lastTok) || before.endsWith(MASK)) return null;       // URL, почта, путь
  if (c === '"') return { del: 0, insert: quoteFor(before) };
  if (c === '.' && before.endsWith('..') && !before.endsWith('...')) return { del: 2, insert: '…' };
  if (c === '.') {
    let m = before.match(/(?<![А-Яа-яЁё])(и|др)[  ]т\.[  ]?([дп])$/);
    if (m) return { del: m[0].length, insert: m[1] + NB + 'т.' + NB + m[2] + '.' };
    m = before.match(/(?<![А-Яа-яЁё])т\.[  ]?([екпдч])$/);
    if (m) return { del: m[0].length, insert: 'т.' + NB + m[1] + '.' };
  }
  if (c === '—') return { del: 0, insert: EN };
  if (c === ' ') {
    if (/[  ]$/.test(before)) return { del: 0, insert: '' };                        // двойной пробел
    let m = before.match(/(?<=[^\s])[  ](?:-{1,2}|—)$/);
    if (m) return { del: m[0].length, insert: NB + EN + ' ' };
    if (/^(?:-{2}|—)$/.test(before)) return { del: before.length, insert: EN + ' ' };
    if (/[№§]$/.test(before)) return { del: 0, insert: NB };
    m = before.match(new RegExp('(?<![\\w.,])(\\d+(?:[.,]\\d+)?) (' + UNITS + ')$'));
    if (m) return { del: m[2].length + 1, insert: NB + m[2] + ' ' };
    m = before.match(/(?<![А-Яа-яЁё])([А-ЯЁ]\.)[  ]?([А-ЯЁ]\.)$/);
    if (m) return { del: m[0].length, insert: m[1] + NB + m[2] + NB };
    return null;
  }
  if (/\d/.test(c)) {
    if (/(?<![\w.\-/:–])\d+(?:[.,]\d+)?-$/.test(before)) return { del: 1, insert: EN + c };
    if (new RegExp('(?<![А-Яа-яЁё])' + REF_WORDS + ' $', 'i').test(before)) return { del: 1, insert: NB + c };
    if (/№$/.test(before)) return { del: 0, insert: NB + c };
    return null;
  }
  if (c === '(' && /(?<![А-Яа-яЁё])формул[а-яё]* $/i.test(before)) return { del: 1, insert: NB + '(' };
  if (/[А-ЯЁ]/.test(c) && /(?<![А-Яа-яЁё])приложени[а-яё]* $/i.test(before)) return { del: 1, insert: NB + c };
  if (c === '%') {
    if (/\d$/.test(before)) return { del: 0, insert: NB + '%' };
    if (/\d $/.test(before)) return { del: 1, insert: NB + '%' };
  }
  if (/[,;:!?)»]/.test(c) && / $/.test(before) && before.trim()) return { del: 1, insert: c };
  return null;
}

// ------------------------------------------------------------------------------------------------ плагин
interface TypoState { last: { from: number; to: number; original: string } | null; justReplaced: boolean }
export const typoKey = new PluginKey<TypoState>('stoTypography');

function inProtected(state: EditorState, pos: number): boolean {
  const $p = state.doc.resolve(pos);
  if ($p.parent.type.spec.code) return true;
  for (let d = $p.depth; d > 0; d--) if (NO_TYPO_TYPES.has($p.node(d).type.name)) return true;
  const marks = state.storedMarks || $p.marks();
  return marks.some((m) => m.type.name === 'code' || m.type.name === 'link');
}

export const TypographyExtension = Extension.create({
  name: 'stoTypography',
  priority: 800,
  addProseMirrorPlugins() {
    return [new Plugin<TypoState>({
      key: typoKey,
      state: {
        init: () => ({ last: null, justReplaced: false }),
        apply: (tr, prev) => {
          const meta = tr.getMeta(typoKey) as Partial<TypoState> | undefined;
          if (meta) return { ...prev, ...meta };
          if (tr.docChanged || tr.selectionSet) return prev.last || prev.justReplaced ? { last: null, justReplaced: false } : prev;
          return prev;
        },
      },
      props: {
        handleTextInput: (view, from, to, text) => {
          const st = typoKey.getState(view.state)!;
          const plain = () => {
            if (!st.justReplaced) return false;
            // после автозамены следующий ввод - новый шаг истории (Ctrl+Z отменит только замену)
            const tr = closeHistory(view.state.tr.insertText(text, from, to));
            tr.setMeta(typoKey, { last: null, justReplaced: false });
            view.dispatch(tr);
            return true;
          };
          if (!prefs.typo || text.length !== 1 || from !== to || inProtected(view.state, from)) return plain();
          const $f = view.state.doc.resolve(from);
          const before = $f.parent.textBetween(Math.max(0, $f.parentOffset - 120), $f.parentOffset, '\n', '￼');
          const r = typedRule(text, before);
          if (!r) return plain();
          if (r.del === 0 && r.insert === text) return plain();
          // 1) набранный символ как есть; 2) замена - отдельным шагом истории
          let tr: Transaction = view.state.tr.insertText(text, from, to);
          if (st.justReplaced) tr = closeHistory(tr);
          view.dispatch(tr);
          const pos = from + text.length;
          const a = pos - text.length - r.del;
          const original = view.state.doc.textBetween(a, pos);
          const tr2 = closeHistory(r.insert ? view.state.tr.insertText(r.insert, a, pos) : view.state.tr.delete(a, pos));
          tr2.setMeta(typoKey, { last: r.insert ? { from: a, to: a + r.insert.length, original } : null, justReplaced: true });
          view.dispatch(tr2);
          return true;
        },
      },
    })];
  },
  addKeyboardShortcuts() {
    return {
      // Backspace сразу после автозамены - вернуть набранное (как в Word)
      Backspace: ({ editor }) => {
        const st = typoKey.getState(editor.state);
        const sel = editor.state.selection;
        if (!st?.last || !sel.empty || sel.from !== st.last.to) return false;
        const { from, to, original } = st.last;
        const tr = editor.state.tr.insertText(original, from, to);
        tr.setMeta(typoKey, { last: null, justReplaced: false });
        editor.view.dispatch(tr);
        return true;
      },
    };
  },
});
